// ActionCenter.js
//
// The review-and-approve queue. Nothing here writes to a customer's site:
// every action is reviewed, approved, then exported as a file the customer
// applies themselves. The copy is deliberate about that.

import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchSummary,
  fetchActions,
  fetchScans,
  generateFromScan,
  updateAction,
  bulkUpdate,
  exportPlan,
  saveAsFile
} from '../services/actionCenterService';
import './ActionCenter.css';

// Sequelize returns JSON columns as an object on some drivers and a raw
// string on others. Accept both so the snippet and notes always render.
function asObject(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (err) {
    return {};
  }
}

function Pill({ kind, children }) {
  return <span className={`ac-pill ${kind}`}>{children}</span>;
}

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    });
  };
  return (
    <button className="ac-copy" onClick={copy}>{done ? 'Copied' : 'Copy'}</button>
  );
}

export default function ActionCenter() {
  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [format, setFormat] = useState('csv');
  const [scanId, setScanId] = useState('');
  const [scans, setScans] = useState([]);
  const [filters, setFilters] = useState({ status: 'pending', severity: '', category: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = {};
      Object.keys(filters).forEach((k) => { if (filters[k]) query[k] = filters[k]; });
      const [list, sum] = await Promise.all([fetchActions(query), fetchSummary()]);
      setActions(list.actions || []);
      setTotal(list.total || 0);
      setSummary(sum);
      setSelected([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your actions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Saved audits for the picker. Defaults to the most recent one.
  useEffect(() => {
    fetchScans()
      .then((list) => {
        setScans(list);
        setScanId((cur) => cur || (list[0] ? String(list[0].id) : ''));
      })
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)));
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.length === actions.length ? [] : actions.map((a) => a.id)));
  };

  const act = async (fn, successMsg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      if (successMsg) console.log(successMsg);
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const setOne = (id, status) => act(() => updateAction(id, status));
  const setMany = (status) => act(() => bulkUpdate(selected, status));

  const generate = () => {
    const id = Number(scanId);
    if (!id) { setError('Enter the scan ID you want to build actions from.'); return; }
    return act(() => generateFromScan(id));
  };

  const runExport = async () => {
    setBusy(true);
    setError('');
    try {
      const ids = selected.length ? selected : undefined;
      const res = await exportPlan({ ids, format });
      saveAsFile(res.filename, res.mime, res.body);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Export failed. Approve at least one action first.');
    } finally {
      setBusy(false);
    }
  };

  const allSelected = selected.length > 0 && selected.length === actions.length;
  const s = summary || {};

  return (
    <div className="ac-wrap">
      <div className="ac-head">
        <div>
          <h1 className="ac-title">Action Center</h1>
          <p className="ac-sub">
            Every issue your audit found, turned into a specific fix with the exact
            change to make. Review what you want, approve it, and export the plan.
          </p>
        </div>
        <div className="ac-bar" style={{ marginBottom: 0 }}>
          <select
            value={scanId}
            onChange={(e) => setScanId(e.target.value)}
            disabled={!scans.length}
          >
            {!scans.length && <option value="">No saved audits yet</option>}
            {scans.map((sc) => (
              <option key={sc.id} value={sc.id}>
                {sc.domain}
                {sc.updatedAt ? ' - ' + new Date(sc.updatedAt).toLocaleDateString() : ''}
              </option>
            ))}
          </select>
          <button className="ac-btn" onClick={generate} disabled={busy || !scanId}>
            Rebuild from audit
          </button>
        </div>
      </div>

      <div className="ac-note">
        Nothing here is applied to your website. The Action Center prepares the
        changes and you publish them, so you stay in control of every edit.
      </div>

      {error && <div className="ac-err">{error}</div>}

      <div className="ac-tiles">
        <div className="ac-tile">
          <div className="ac-tile-n">{s.pending || 0}</div>
          <div className="ac-tile-l">Open</div>
        </div>
        <div className="ac-tile crit">
          <div className="ac-tile-n">{s.criticalOpen || 0}</div>
          <div className="ac-tile-l">Critical</div>
        </div>
        <div className="ac-tile win">
          <div className="ac-tile-n">{s.quickWins || 0}</div>
          <div className="ac-tile-l">Quick wins</div>
        </div>
        <div className="ac-tile">
          <div className="ac-tile-n">{s.approved || 0}</div>
          <div className="ac-tile-l">Approved</div>
        </div>
        <div className="ac-tile">
          <div className="ac-tile-n">{s.resolved || 0}</div>
          <div className="ac-tile-l">Resolved</div>
        </div>
      </div>

      <div className="ac-bar">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="pending">Open</option>
          <option value="approved">Approved</option>
          <option value="exported">Exported</option>
          <option value="applied">Applied</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <span className="ac-spacer" />
        <select value={format} onChange={(e) => setFormat(e.target.value)}>
          <option value="csv">CSV</option>
          <option value="markdown">Markdown</option>
          <option value="json">JSON</option>
        </select>
        <button className="ac-btn primary" onClick={runExport} disabled={busy}>
          Export {selected.length ? `${selected.length} selected` : 'approved'}
        </button>
      </div>

      {selected.length > 0 && (
        <div className="ac-selbar">
          <strong>{selected.length} selected</strong>
          <button className="ac-btn" onClick={() => setMany('approved')} disabled={busy}>Approve</button>
          <button className="ac-btn" onClick={() => setMany('applied')} disabled={busy}>Mark applied</button>
          <button className="ac-btn danger" onClick={() => setMany('dismissed')} disabled={busy}>Dismiss</button>
          <span className="ac-spacer" />
          <button className="ac-btn" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      {loading && <div className="ac-empty">Loading your actions...</div>}

      {!loading && actions.length === 0 && (
        <div className="ac-empty">
          <p><strong>Nothing queued yet.</strong></p>
          <p>Run a site audit and your fixes appear here automatically. You can also pick a saved audit above and rebuild.</p>
        </div>
      )}

      {!loading && actions.length > 0 && (
        <React.Fragment>
          <div className="ac-bar">
            <label style={{ fontSize: 13, color: '#64748b', display: 'flex', gap: 7, alignItems: 'center' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              Select all {actions.length} shown
            </label>
            <span className="ac-spacer" />
            <span style={{ fontSize: 13, color: '#64748b' }}>{total} total</span>
          </div>

          {actions.map((a) => {
            const impl = asObject(a.implementation);
            const open = expanded === a.id;
            return (
              <div key={a.id} className={open ? 'ac-row open' : 'ac-row'}>
                <input
                  type="checkbox"
                  checked={selected.includes(a.id)}
                  onChange={() => toggle(a.id)}
                />
                <div className="ac-main" onClick={() => setExpanded(open ? null : a.id)}>
                  <div className="ac-rtitle">{a.title}</div>
                  <div className="ac-meta">
                    <Pill kind={a.severity}>{a.severity}</Pill>
                    {!a.autoFixable && <Pill kind="advisory">advisory</Pill>}
                    <span className="ac-score">{a.impactScore}/100</span>
                    <span>{a.effort}</span>
                    <span>{a.category}</span>
                    {a.pageUrl && <span className="ac-url">{a.pageUrl}</span>}
                    {a.status !== 'pending' && <span>{a.status}</span>}
                  </div>

                  {open && (
                    <div className="ac-detail">
                      {a.rationale && <div className="ac-why">{a.rationale}</div>}
                      <div className="ac-diff">
                        <div className="ac-box before">
                          <h5>Now</h5>
                          <p className="ac-code">{a.currentValue || 'Not set'}</p>
                        </div>
                        <div className="ac-box after">
                          <h5>Proposed</h5>
                          <p className="ac-code">{a.proposedValue || 'See notes'}</p>
                        </div>
                      </div>
                      {impl.snippet && (
                        <div className="ac-snip">
                          <CopyButton text={impl.snippet} />
                          <pre className="ac-code">{impl.snippet}</pre>
                        </div>
                      )}
                      <div className="ac-notes">
                        {impl.placement && <div><strong>Where:</strong> {impl.placement}</div>}
                        {impl.wordpress && <div><strong>WordPress:</strong> {impl.wordpress}</div>}
                        {impl.notes && <div><strong>Note:</strong> {impl.notes}</div>}
                      </div>
                      <div className="ac-acts" onClick={(e) => e.stopPropagation()}>
                        <button className="ac-btn primary" onClick={() => setOne(a.id, 'approved')} disabled={busy}>Approve</button>
                        <button className="ac-btn" onClick={() => setOne(a.id, 'applied')} disabled={busy}>Mark applied</button>
                        <button className="ac-btn danger" onClick={() => setOne(a.id, 'dismissed')} disabled={busy}>Dismiss</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </React.Fragment>
      )}
    </div>
  );
}
